export const resizeImage = (
  file: File,
  callback: (dataUrl: string) => void,
  maxWidth = 300,
  maxHeight = 400,
  quality = 0.6
) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
      }
      
      // Siempre usamos webp, incluso para PNG, ya que webp retiene la transparencia (alpha) 
      // pero permite una compresión bestial a diferencia de png que es lossless en dataURL.
      // Si el navegador no soporta webp (ej Safari antiguo), toDataURL ignorará el formato y regresará png, 
      // pero para la gran mayoría, reducirá drásticamente el uso de Firebase.
      callback(canvas.toDataURL('image/webp', quality));
    };
    img.src = e.target?.result as string;
  };
  reader.readAsDataURL(file);
};

export const getRgba = (colorStr: string, alphaPercent: number) => {
  const alpha = (alphaPercent !== undefined && alphaPercent !== null) ? alphaPercent / 100 : 1;
  if (!colorStr) return `rgba(0,0,0,${alpha})`;
  if (colorStr.startsWith('#')) {
    let r = 0,
      g = 0,
      b = 0;
    if (colorStr.length === 4) {
      r = parseInt(colorStr[1] + colorStr[1], 16);
      g = parseInt(colorStr[2] + colorStr[2], 16);
      b = parseInt(colorStr[3] + colorStr[3], 16);
    } else if (colorStr.length >= 7) {
      r = parseInt(colorStr.slice(1, 3), 16);
      g = parseInt(colorStr.slice(3, 5), 16);
      b = parseInt(colorStr.slice(5, 7), 16);
    }
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return colorStr;
};

/**
 * Formatea el nombre de un jugador según el nivel de detalle requerido.
 * @param fullName Nombre completo del jugador
 * @param type 'full' | 'short' (J. Perez) | 'minimal' (Perez)
 */
export const formatPlayerName = (fullName: string, type: 'full' | 'short' | 'minimal' = 'full') => {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1 || type === 'full') return fullName;

  const firstName = parts[0];
  const lastName = parts[parts.length - 1];

  if (type === 'short') {
    return `${firstName.charAt(0)}. ${lastName}`;
  }

  if (type === 'minimal') {
    return lastName;
  }

  return fullName;
};

/**
 * Formatea el nombre de una pareja (Equipo).
 * Asume el formato "Jugador 1 & Jugador 2" o similar.
 * @param teamName Nombre del equipo
 * @param type 'full' | 'short' | 'minimal'
 * @param separator Separador entre jugadores (default '/')
 */
export const formatPairName = (teamName: string, type: 'full' | 'short' | 'minimal' = 'full', separator: string = '/') => {
  if (!teamName) return '';
  
  // Detectar separador original (suele ser " & " o " / ")
  const originalSeparator = teamName.includes(' & ') ? ' & ' : teamName.includes(' / ') ? ' / ' : null;
  
  if (!originalSeparator || type === 'full') return teamName;

  const players = teamName.split(originalSeparator);
  if (players.length !== 2) return teamName;

  const p1 = formatPlayerName(players[0], type);
  const p2 = formatPlayerName(players[1], type);

  return `${p1} ${separator} ${p2}`;
};

/**
 * Devuelve la fecha "operativa" en formato YYYY-MM-DD.
 * Si la hora actual es menor a la hora de cierre (ej. 03:30 AM),
 * se considera que todavía estamos en el "día anterior".
 */
export const getOperatingDateStr = (date: Date, closeTimeStr?: string): string => {
  if (!closeTimeStr) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  const [closeHour, closeMinute] = closeTimeStr.split(':').map(Number);
  const hour = date.getHours();
  const minute = date.getMinutes();

  const isBeforeCloseTime = hour < closeHour || (hour === closeHour && minute < closeMinute);

  const targetDate = new Date(date);
  if (isBeforeCloseTime) {
    targetDate.setDate(targetDate.getDate() - 1);
  }

  const yyyy = targetDate.getFullYear();
  const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
  const dd = String(targetDate.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Limpia y formatea un número de teléfono argentino para que sea compatible con WhatsApp (Green API).
 * Asegura el prefijo 549 y elimina el 15.
 * @param rawPhone Número de teléfono introducido por el usuario
 * @returns El número listo para Green API (ej: 5491145678900@c.us) o null si es inválido
 */
export const formatArgentineWhatsApp = (rawPhone?: string): string | null => {
  if (!rawPhone) return null;

  // 1. Limpiar todo lo que no sea número
  let cleaned = rawPhone.replace(/\D/g, '');

  // 2. Si empieza con 54, quitarlo momentáneamente para normalizar
  if (cleaned.startsWith('549')) {
    cleaned = cleaned.substring(3);
  } else if (cleaned.startsWith('54')) {
    cleaned = cleaned.substring(2);
  }

  // 3. Quitar el "0" local al principio si lo tiene (ej: 011 -> 11)
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }

  // 4. Buscar y quitar el "15" después del código de área.
  // Los códigos de área en Argentina tienen 2, 3 o 4 dígitos.
  // Las longitudes de los códigos de área + números sin el 15 es siempre 10.
  // Si tiene 12 dígitos y el 15 está en la posición 2, 3 o 4, lo sacamos.
  if (cleaned.length === 12) {
    if (cleaned.substring(2, 4) === '15') {
      cleaned = cleaned.substring(0, 2) + cleaned.substring(4);
    } else if (cleaned.substring(3, 5) === '15') {
      cleaned = cleaned.substring(0, 3) + cleaned.substring(5);
    } else if (cleaned.substring(4, 6) === '15') {
      cleaned = cleaned.substring(0, 4) + cleaned.substring(6);
    }
  }

  // 5. Verificamos que queden 10 dígitos (Código de área + número local)
  if (cleaned.length !== 10) {
    return null; // Número inválido
  }

  // 6. Armamos el formato final para WhatsApp API
  return `549${cleaned}@c.us`;
};

